import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { ApiError } from '../../../../adapters/api-client/api';
import { StudentTeacherProfile, getLearnerTeacher } from '../../api/learner-explore';
import { getTeacherReviews } from '../../api/reviews';
import ReviewsSection from '../../components/ReviewsSection';

type Load =
  | { status: 'loading' }
  | { status: 'missing' }
  | { status: 'error' }
  | { status: 'ready'; teacher: StudentTeacherProfile };

/**
 * UC-S05 — the teacher, as a Student reads them (DEC-S2-S05-01..08).
 *
 * The Parent's approved public profile through a Student filter on the server:
 * no contact of any kind, no trust score (DEC-S2-S05-05). The verified badge is
 * the server's. Reviews are read-only and the teacher's rating stays on this
 * page, apart from any course's (DEC-S2-S05-03, S05-07). Ask a Question is
 * disabled until safeguarded messaging exists (DEC-S2-S05-06).
 */
const LearnerTeacher: React.FC = () => {
  const { teacherId = '' } = useParams<{ teacherId: string }>();
  const [load, setLoad] = useState<Load>({ status: 'loading' });

  const fetchTeacher = useCallback(() => {
    setLoad({ status: 'loading' });
    getLearnerTeacher(teacherId)
      .then((teacher) => setLoad({ status: 'ready', teacher }))
      .catch((error: unknown) =>
        setLoad({
          status: error instanceof ApiError && error.status === 404 ? 'missing' : 'error',
        }),
      );
  }, [teacherId]);

  useEffect(fetchTeacher, [fetchTeacher]);

  return (
    <>
      <p>
        <Link className="lrn-link" to="/student/explore">
          ← Explorar clases
        </Link>
      </p>

      {load.status === 'loading' && (
        <p className="hi-s" role="status">
          Cargando…
        </p>
      )}

      {load.status === 'missing' && (
        <div className="lrn-status" role="status">
          <h2>Este perfil no está disponible</h2>
          <p>Hay más clases para explorar.</p>
        </div>
      )}

      {load.status === 'error' && (
        <div className="lrn-status" role="alert">
          <h2>No pudimos cargar este perfil</h2>
          <p>No es culpa tuya. Prueba otra vez en un momento.</p>
          <button className="ag-b" onClick={fetchTeacher}>
            Intentar otra vez
          </button>
        </div>
      )}

      {load.status === 'ready' && <TeacherBody teacher={load.teacher} />}
    </>
  );
};

const TeacherBody: React.FC<{ teacher: StudentTeacherProfile }> = ({ teacher }) => (
  <>
    <div className="lrn-status">
      <h1 className="h2">
        {teacher.firstName} {teacher.lastName}
      </h1>
      {teacher.verified && <span className="lrn-tag">Verificado</span>}
      {teacher.bio && <p>{teacher.bio}</p>}
      {teacher.methodology && <p>{teacher.methodology}</p>}
      {teacher.subjects.length > 0 && <p>Enseña: {teacher.subjects.join(', ')}</p>}
      {teacher.languages.length > 0 && <p>Idiomas: {teacher.languages.join(', ')}</p>}
      <p>
        <button type="button" className="ag-b" disabled>
          Hacer una pregunta
        </button>
      </p>
      <p>Las preguntas todavía no están disponibles.</p>
    </div>

    <div className="lrn-status">
      <h2>Otras clases</h2>
      {teacher.publishedCourses === null && <p>No pudimos cargar sus clases.</p>}
      {teacher.publishedCourses?.length === 0 && <p>Ahora no tiene clases publicadas.</p>}
      {teacher.publishedCourses?.map((course) => (
        <p key={course.courseId}>
          <Link className="lrn-link" to={`/student/explore/${encodeURIComponent(course.courseId)}`}>
            {course.title}
          </Link>
        </p>
      ))}
    </div>

    <ReviewsSection
      subject="TEACHER"
      subjectId={teacher.teacherId}
      load={getTeacherReviews}
      title="Reseñas del profesor"
    />
  </>
);

export default LearnerTeacher;
